import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'components/ui/toast-store'
import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import MyDropzone from 'components/MyDropzone/MyDropzone'
import BulkLoadMap from './BulkLoadMap'
import LocationEditPopupCN from 'components/popups/LocationEditPopupCN'
import BulkLocationsAddPopup from 'components/popups/BulkLocationsAddPopup'
import BulkTenantsAddPopup from 'components/popups/BulkTenantsAddPopup'
import LocationAddNew from 'screens/locations/components/LocationAddNew'
import {
	ChevronUp,
	ChevronDown,
	ArrowUpDown,
	MapPin as MapPinIcon,
	Building2,
	XCircle,
	List,
	Map,
	Sparkles,
	CheckCircle2,
	Circle,
	Trash2,
	Upload,
	RotateCw,
	Loader2,
} from 'lucide-react'
import geocodeAddress from 'functions/geocodeAddress'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { Button } from 'components/ui/button'
import { Sheet } from 'components/ui/sheet'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from 'components/ui/table'
import { TablePagination } from 'components/ui/pagination'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog'
import {
	createBulkLocations,
	createLocation,
	initializeActiveLocations,
	initializeMyCenters,
} from 'slices/locationSlice'
import {
	deleteAllLocations,
	deleteOneLocation,
	initializeBulkLocations,
	updateBulkLocation,
} from 'slices/bulkLocationSlice'
import { initializeApiKey } from 'slices/apiKeysSlice'
import { uploadToCloudinary } from 'components/FileUploader/fileUploadUtils'
import MapLocationPopupCN from 'components/popups/MapLocationPopupCN'
import notify from 'components/ui/notify'

const headerCells = [
	{ id: 'image', label: '', sortable: false, width: 'w-10' },
	{ id: 'assetName', label: 'Location' },
	{ id: 'retailer', label: 'Retailer' },
	{ id: 'address', label: 'Address' },
	{ id: 'city', label: 'City' },
	{ id: 'stateProvince', label: 'State', width: 'w-16' },
	{ id: 'postalCode', label: 'Zip', width: 'w-24' },
	{ id: 'googlePlaceId', label: 'GP', sortable: false, width: 'w-10' },
	{ id: 'delete', label: '', sortable: false, width: 'w-10' },
]

const BulkLoadLocationsCN = () => {
	const dispatch = useDispatch()
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const [openMap, setOpenMap] = useState(false)
	const [mapLocation, setMapLocation] = useState<any>(null)
	const [viewMode, setViewMode] = useState<'table' | 'map'>('table')
	const [isEnriching, setIsEnriching] = useState(false)
	const [enrichProgress, setEnrichProgress] = useState({ current: 0, total: 0 })
	const placesRef = useRef<HTMLDivElement>(null)

	const { userInfo } = useSelector((state: any) => state.userLogin)
	const userId = userInfo?._id

	const searchInputRef = useRef<HTMLInputElement>(null)
	const [searchInputValue, setSearchInputValue] = useState('')

	const [openModifyPopup, setOpenModifyPopup] = useState(false)
	const [openAddLocationPopup, setOpenAddLocationPopup] = useState(false)
	const [openPostLocationsPopup, setOpenPostLocationsPopup] = useState(false)
	const [openPostTenantsPopup, setOpenPostTenantsPopup] = useState(false)
	const [popupTitle, setPopupTitle] = useState('')
	const [recordForEdit, setRecordForEdit] = useState<any>(null)
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
	const [regeocodingIds, setRegeocodingIds] = useState<Set<string>>(new Set())

	const [orderBy, setOrderBy] = useState<string | null>(null)
	const [order, setOrder] = useState<'asc' | 'desc'>('asc')
	const [page, setPage] = useState(0)
	const [rowsPerPage, setRowsPerPage] = useState(10)

	const { entity: apiKeyState } = useSelector((state: any) => state.apiKey)
	const apiKey = apiKeyState?.key

	const {
		entities: locations,
		ids: locationIds,
		isLoading,
		isInitialized,
	} = useSelector((state: any) => state.bulkLocations)
	const records = locationIds.map((id: string) => locations[id])

	const { entities, ids: centerIds } = useSelector((state: any) => state.locations)
	const centers = centerIds.map((id: string) => entities[id])

	const myCenters = centers.map((center: any) => ({
		selectId: center._id,
		selectValue: center.assetName,
	}))

	useEffect(() => {
		// @ts-ignore
		dispatch(initializeMyCenters())
		if (!isInitialized) {
			// @ts-ignore
			dispatch(initializeBulkLocations())
		}
		// @ts-ignore
		dispatch(initializeApiKey('REACT_APP_GOOGLE_API_KEY'))
	}, [dispatch, isInitialized])

	const hasRows = records && records.length > 0
	const hasCenters = myCenters && myCenters.length > 0

	const handleRegeocode = useCallback(
		async (location: any) => {
			if (!location?._id) return
			const id = location._id
			setRegeocodingIds(prev => {
				const next = new Set(prev)
				next.add(id)
				return next
			})
			// Match MyDropzone: prefer street-address query for Nominatim, fall
			// back to assetName only when no street address is available.
			const trim = (s: any) => (s == null ? '' : String(s).trim())
			const street = trim(location.address)
			const city = trim(location.city)
			const state = trim(location.stateProvince)
			const zip = trim(location.postalCode)
			const assetName = trim(location.assetName)
			const query = street
				? [street, city, state, zip].filter(Boolean).join(', ')
				: [assetName, city, state, zip].filter(Boolean).join(', ')
			try {
				const { lat, lng } = await geocodeAddress(query)
				// @ts-ignore
				await dispatch(updateBulkLocation({ ...location, latitude: lat, longitude: lng }))
			} catch (err) {
				console.warn(`Re-geocode failed for "${query}":`, err)
			} finally {
				setRegeocodingIds(prev => {
					const next = new Set(prev)
					next.delete(id)
					return next
				})
			}
		},
		[dispatch],
	)

	const handleSort = (columnId: string) => {
		if (orderBy === columnId) {
			setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setOrderBy(columnId)
			setOrder('asc')
		}
	}

	const filteredRecords = useMemo(() => {
		if (!records || records.length === 0) return []
		if (!searchInputValue) return records
		const lowerValue = searchInputValue.toLowerCase()
		return records.filter(
			(x: any) =>
				(x.assetName && x.assetName.toLowerCase().includes(lowerValue)) ||
				(x.retailer && x.retailer.toLowerCase().includes(lowerValue)) ||
				(x.address && x.address.toLowerCase().includes(lowerValue)) ||
				(x.city && x.city.toLowerCase().includes(lowerValue)),
		)
	}, [records, searchInputValue])

	const sortedRecords = useMemo(() => {
		if (!orderBy) return filteredRecords
		return [...filteredRecords].sort((a: any, b: any) => {
			const aVal = (a[orderBy] || '').toString().toLowerCase()
			const bVal = (b[orderBy] || '').toString().toLowerCase()
			if (aVal < bVal) return order === 'asc' ? -1 : 1
			if (aVal > bVal) return order === 'asc' ? 1 : -1
			return 0
		})
	}, [filteredRecords, orderBy, order])

	const paginatedRecords = useMemo(() => {
		const start = page * rowsPerPage
		return sortedRecords.slice(start, start + rowsPerPage)
	}, [sortedRecords, page, rowsPerPage])

	// Map data: build markers and center from filtered records
	const mapData = useMemo(() => {
		const pins = sortedRecords
			.filter((loc: any) => loc.latitude && loc.longitude)
			.map((loc: any) => ({
				id: loc._id,
				lat: loc.latitude,
				lng: loc.longitude,
				name: loc.assetName,
				address: loc.address,
				city: loc.city,
				state: loc.stateProvince,
				postalCode: loc.postalCode,
				image: loc.image,
			}))

		if (pins.length === 0) return { pins: [], center: [39.5, -98.35], zoom: 4 }

		if (pins.length === 1) {
			return { pins, center: [pins[0].lat, pins[0].lng], zoom: 12 }
		}

		const lats = pins.map((p: any) => p.lat)
		const lngs = pins.map((p: any) => p.lng)
		const centerLat = (Math.max(...lats) + Math.min(...lats)) / 2
		const centerLng = (Math.max(...lngs) + Math.min(...lngs)) / 2

		return { pins, center: [centerLat, centerLng], zoom: 10 }
	}, [sortedRecords])

	const SortIcon = ({ columnId }: { columnId: string }) => {
		if (orderBy !== columnId) return <ArrowUpDown className="ml-1 h-3 w-3 text-gray-300" />
		return order === 'asc' ? (
			<ChevronUp className="ml-1 h-3 w-3 text-[#0080fe]" />
		) : (
			<ChevronDown className="ml-1 h-3 w-3 text-[#0080fe]" />
		)
	}

	const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchInputValue(e.target.value)
		setPage(0)
	}

	const handleOpenEditPopup = (location: any) => {
		setRecordForEdit(location)
		setShowDeleteConfirm(false)
		setOpenModifyPopup(true)
	}

	const handleEditLocation = async (newLocation: any) => {
		// @ts-ignore
		await dispatch(updateBulkLocation(newLocation))
		setOpenModifyPopup(false)
	}

	const handleAddLocation = async (location: any, resetForm: () => void) => {
		const newLocation = location
		newLocation.owner = userId

		if (newLocation.locationType) {
			const locationType = newLocation.locationType.toLocaleLowerCase()
			const newAssetType = locationType.split(' ')
			if (newAssetType[1]) newLocation.assetType = newAssetType[1]
		}
		// @ts-ignore
		await dispatch(createLocation({ newLocation }))
		// @ts-ignore
		dispatch(initializeActiveLocations())
		resetForm()
		setOpenAddLocationPopup(false)
	}

	const actionPostLocations = () => {
		setPopupTitle('Post as Locations:')
		setOpenPostLocationsPopup(true)
	}

	const actionPostTenants = () => {
		if (hasCenters) {
			setPopupTitle('Post as Tenants:')
			setOpenPostTenantsPopup(true)
		} else {
			notify({
				title: `No Retail Centers!`,
				text: `You must first create a retail center before you can post this list as tenants.`,
				icon: 'warning',
				buttons: {
					confirm: {
						text: 'Ok',
						value: true,
						visible: true,
						className: '',
						closeModal: true,
					},
				},
			})
			setPopupTitle('Add Retail Center Location')
			setOpenAddLocationPopup(true)
		}
	}

	const handlePostBulkLocations = async () => {
		// @ts-ignore
		await dispatch(createBulkLocations(records))
		setOpenPostLocationsPopup(false)
	}

	const handlePostFleetLocations = async (retailCenter: any) => {
		// @ts-ignore
		await dispatch(updateBulkLocation(retailCenter))
		setOpenPostTenantsPopup(false)
	}

	const handleClearList = () => {
		notify({
			title: `Clear List!`,
			text: `This will clear the current import list without posting the new locations.`,
			icon: 'success',
			buttons: {
				confirm: {
					text: 'Confirm',
					value: true,
					visible: true,
					className: '',
					closeModal: true,
				},
				cancel: {
					text: 'Cancel',
					value: null,
					visible: true,
					className: '',
					closeModal: true,
				},
			},
		}).then((willDelete: boolean) => {
			if (willDelete) {
				notify({
					title: `List Cleared!`,
					text: `The current list has been cleared.`,
					icon: 'success',
				})
				// @ts-ignore
				dispatch(deleteAllLocations())
			}
		})
		// @ts-ignore
		dispatch(initializeBulkLocations())
	}

	const handleDeleteDetail = async (location: any) => {
		if (await toast.confirm({ title: 'Delete location', message: 'Delete this location?' })) {
			// @ts-ignore
			await dispatch(deleteOneLocation(location._id))
			// @ts-ignore
			dispatch(initializeBulkLocations())
		}
	}

	// Google Places enrichment: fetch googlePlaceId, image, and retailer for each location
	const enrichWithGooglePlaces = useCallback(async () => {
		if (!placesRef.current || isEnriching) return

		const toEnrich = records.filter((loc: any) => !loc.googlePlaceId)
		if (toEnrich.length === 0) {
			notify({ title: 'All Done', text: 'All locations already have Google Place data.', icon: 'info' })
			return
		}

		setIsEnriching(true)
		setEnrichProgress({ current: 0, total: toEnrich.length })

		const service = new (window as any).google.maps.places.PlacesService(placesRef.current)
		const OK = (window as any).google.maps.places.PlacesServiceStatus.OK

		for (let i = 0; i < toEnrich.length; i++) {
			const loc = toEnrich[i]
			setEnrichProgress({ current: i + 1, total: toEnrich.length })

			try {
				const enriched = await enrichSingleLocation(service, OK, loc)
				if (enriched) {
					// @ts-ignore
					await dispatch(updateBulkLocation(enriched))
				}
			} catch (err) {
				console.error(`[Enrich] Failed for "${loc.assetName}":`, err)
			}

			// Small delay between requests to avoid rate limits
			if (i < toEnrich.length - 1) {
				await new Promise(resolve => setTimeout(resolve, 300))
			}
		}

		setIsEnriching(false)
		// @ts-ignore
		dispatch(initializeBulkLocations())
	}, [records, isEnriching, dispatch])

	const renderContent = () => {
		if (isLoading) {
			return (
				<div className="flex items-center justify-center py-20">
					<div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#0080fe]" />
				</div>
			)
		}

		if (!hasRows) {
			return (
				<div className="max-w-[1080px] mx-auto w-full mt-4">
					<MyDropzone apiKey={apiKey} />
				</div>
			)
		}

		return (
			<>
				<div className="max-w-[1080px] mx-auto w-full mt-4 bg-white rounded-lg border border-gray-200 p-5">
					{/* Toolbar */}
					<div className="mb-4 flex items-center justify-between gap-4">
						<div className="flex items-center gap-3">
							<input
								ref={searchInputRef}
								type="text"
								placeholder="Search by location, retailer, address, or city..."
								value={searchInputValue}
								onChange={handleSearch}
								className="w-full max-w-sm rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 placeholder-gray-400 focus:border-[#0080fe] focus:outline-none focus:ring-1 focus:ring-[#0080fe]"
							/>
						</div>
						<div className="flex items-center gap-2">
							{/* View Toggle */}
							<div className="inline-flex items-center rounded-lg bg-gray-100 p-0.5">
								<button
									type="button"
									onClick={() => setViewMode('table')}
									className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
										viewMode === 'table'
											? 'bg-white text-[#0080fe] shadow-sm'
											: 'text-gray-500 hover:text-gray-700'
									}`}
								>
									<List className="h-3.5 w-3.5" />
									Table
								</button>
								<button
									type="button"
									onClick={() => setViewMode('map')}
									className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
										viewMode === 'map'
											? 'bg-white text-[#0080fe] shadow-sm'
											: 'text-gray-500 hover:text-gray-700'
									}`}
								>
									<Map className="h-3.5 w-3.5" />
									Map
								</button>
							</div>
							<button
								type="button"
								onClick={enrichWithGooglePlaces}
								disabled={isEnriching}
								className="inline-flex items-center gap-1.5 rounded-lg border border-[#0080fe] bg-white px-3 py-2 text-xs font-medium text-[#0080fe] hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
								title="Enrich with Google Places"
							>
								<Sparkles className="h-3 w-3" />
								{isEnriching
									? `Enriching ${enrichProgress.current}/${enrichProgress.total}...`
									: 'Enrich with Google'}
							</button>
							<button
								type="button"
								onClick={actionPostLocations}
								className="inline-flex items-center gap-1.5 rounded-lg bg-[#0080fe] px-3 py-2 text-xs font-medium text-white hover:bg-[#0070e0] transition-colors"
								title="Post as Locations"
							>
								<MapPinIcon className="h-3 w-3" />
								Post as Locations
							</button>
							<button
								type="button"
								onClick={actionPostTenants}
								className="inline-flex items-center gap-1.5 rounded-lg bg-[#0080fe] px-3 py-2 text-xs font-medium text-white hover:bg-[#0070e0] transition-colors"
								title="Post as Tenants"
							>
								<Building2 className="h-3 w-3" />
								Post as Tenants
							</button>
							<button
								type="button"
								onClick={handleClearList}
								className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
								title="Clear List"
							>
								Clear List
							</button>
						</div>
					</div>

					{/* Map View */}
					{viewMode === 'map' && (
						<div className="rounded-lg border border-gray-200 overflow-hidden">
							{mapData.pins.length > 0 ? (
								<BulkLoadMap
									markers={mapData.pins}
									center={mapData.center}
									zoom={mapData.zoom}
								/>
							) : (
								<div className="flex items-center justify-center py-20 text-gray-400">
									<div className="text-center">
										<Map className="h-10 w-10 mx-auto mb-2 opacity-40" />
										<p className="text-sm">No locations with coordinates to display on the map.</p>
									</div>
								</div>
							)}
						</div>
					)}

					{/* Table View */}
					{viewMode === 'table' && (
						<>
							<div className="rounded-lg border border-gray-200 overflow-x-clip [&>div]:overflow-x-clip">
								<Table className="table-fixed w-full">
									<TableHeader>
										<TableRow className="border-b-2 border-[#0080fe] hover:bg-transparent">
											{headerCells.map(cell => (
												<TableHead
													key={cell.id}
													className={[
														cell.sortable !== false ? 'cursor-pointer select-none' : '',
														'text-gray-700 text-xs font-semibold whitespace-nowrap',
														cell.width || '',
													].join(' ')}
													onClick={cell.sortable !== false ? () => handleSort(cell.id) : undefined}
												>
													<span className="inline-flex items-center">
														{cell.label}
														{cell.sortable !== false && cell.label && <SortIcon columnId={cell.id} />}
													</span>
												</TableHead>
											))}
										</TableRow>
									</TableHeader>
									<TableBody>
										{paginatedRecords.map((location: any) => (
											<TableRow key={location._id} className="h-7 hover:bg-[#fffbf2]">
												<TableCell className="py-1">
													<Avatar className="h-7 w-7">
														<AvatarImage src={location.image} />
														<AvatarFallback className="text-[10px] bg-gray-100 text-gray-400">
															{location.assetName?.[0] || '?'}
														</AvatarFallback>
													</Avatar>
												</TableCell>
												<TableCell
													className="py-1 text-sm truncate cursor-pointer"
													onClick={() => handleOpenEditPopup(location)}
												>
													{location.assetName}
												</TableCell>
												<TableCell
													className="py-1 text-sm truncate cursor-pointer"
													onClick={() => handleOpenEditPopup(location)}
												>
													{location.retailer}
												</TableCell>
												<TableCell className="py-1 text-sm">
													{location.latitude && location.longitude ? (
														<button
															type="button"
															onClick={e => {
																e.stopPropagation()
																setMapLocation(location)
																setOpenMap(true)
															}}
															className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs text-gray-600 hover:bg-[#0080fe]/10 hover:text-[#0080fe] hover:border-[#0080fe]/20 transition-colors cursor-pointer"
														>
															<MapPinIcon className="h-3 w-3" />
															{location.address}
														</button>
													) : (
														<span className="inline-flex items-center gap-1.5 max-w-full">
															<span
																className="truncate cursor-pointer"
																onClick={() => handleOpenEditPopup(location)}
															>
																{location.address}
															</span>
															<button
																type="button"
																onClick={e => {
																	e.stopPropagation()
																	handleRegeocode(location)
																}}
																disabled={regeocodingIds.has(location._id)}
																title="Re-geocode this address"
																className="shrink-0 inline-flex items-center justify-center w-5 h-5 rounded text-gray-400 hover:text-[#0080fe] hover:bg-[#0080fe]/10 disabled:opacity-50 disabled:cursor-wait transition-colors"
															>
																{regeocodingIds.has(location._id) ? (
																	<Loader2 className="h-3 w-3 animate-spin" />
																) : (
																	<RotateCw className="h-3 w-3" />
																)}
															</button>
														</span>
													)}
												</TableCell>
												<TableCell
													className="py-1 text-sm truncate cursor-pointer"
													onClick={() => handleOpenEditPopup(location)}
												>
													{location.city}
												</TableCell>
												<TableCell
													className="py-1 text-sm cursor-pointer"
													onClick={() => handleOpenEditPopup(location)}
												>
													{location.stateProvince}
												</TableCell>
												<TableCell
													className="py-1 text-sm cursor-pointer"
													onClick={() => handleOpenEditPopup(location)}
												>
													{location.postalCode}
												</TableCell>
												<TableCell className="py-1 text-sm text-center">
													{location.googlePlaceId ? (
														<CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" />
													) : (
														<Circle className="h-4 w-4 text-gray-300 mx-auto" />
													)}
												</TableCell>
												<TableCell className="py-1 text-sm">
													<button
														type="button"
														onClick={() => handleDeleteDetail(location)}
														className="text-gray-400 hover:text-red-500 transition-colors"
														title="Delete"
													>
														<XCircle className="h-4 w-4" />
													</button>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>

							{sortedRecords.length > 4 && (
								<TablePagination
									page={page}
									rowsPerPage={rowsPerPage}
									count={sortedRecords.length}
									onPageChange={setPage}
									onRowsPerPageChange={setRowsPerPage}
								/>
							)}
						</>
					)}
				</div>

				{/* Edit Sheet */}
				<Sheet open={openModifyPopup} onClose={() => setOpenModifyPopup(false)} side="right" className="!w-[650px]">
					{recordForEdit ? (
						<>
							{/* Header */}
							<div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
								<div className="min-w-0 flex-1">
									<h2 className="text-lg font-semibold text-gray-900 truncate">{recordForEdit.assetName}</h2>
									<p className="text-sm text-gray-500 truncate">
										{recordForEdit.address}, {recordForEdit.city}, {recordForEdit.stateProvince}
									</p>
								</div>
								<button
									type="button"
									onClick={() => setOpenModifyPopup(false)}
									className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors ml-3 flex-shrink-0"
									title="Close (Esc)"
								>
									<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							</div>

							{/* Body — Edit Form */}
							<div className="flex-1 overflow-y-auto p-5">
								<LocationEditPopupCN editLocation={handleEditLocation} recordForEdit={recordForEdit} />
							</div>

							{/* Footer — Delete */}
							<div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex items-center">
								{showDeleteConfirm ? (
									<div className="flex items-center gap-2">
										<span className="text-sm text-red-600">Delete this location?</span>
										<Button
											variant="danger"
											onClick={() => {
												handleDeleteDetail(recordForEdit)
												setOpenModifyPopup(false)
												setShowDeleteConfirm(false)
											}}
										>
											Yes, Delete
										</Button>
										<Button variant="ghost" onClick={() => setShowDeleteConfirm(false)}>
											Cancel
										</Button>
									</div>
								) : (
									<Button variant="ghost-red" onClick={() => setShowDeleteConfirm(true)}>
										<Trash2 className="w-4 h-4 mr-1" />
										Delete
									</Button>
								)}
							</div>
						</>
					) : (
						<div />
					)}
				</Sheet>

				{/* Post as Locations Dialog */}
				<Dialog open={openPostLocationsPopup} onClose={() => setOpenPostLocationsPopup(false)}>
					<DialogContent className="max-w-md">
						<DialogHeader>
							<div className="flex items-center gap-3">
								<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
									<Upload className="w-5 h-5" />
								</div>
								<div>
									<DialogTitle>{popupTitle}</DialogTitle>
									<DialogDescription>Bulk import locations</DialogDescription>
								</div>
							</div>
						</DialogHeader>
						<div className="p-5">
							<BulkLocationsAddPopup
								handlePostBulkLocations={handlePostBulkLocations}
								locationCount={records.length}
							/>
						</div>
					</DialogContent>
				</Dialog>

				{/* Post as Tenants Dialog */}
				<Dialog open={openPostTenantsPopup} onClose={() => setOpenPostTenantsPopup(false)}>
					<DialogContent className="max-w-md">
						<DialogHeader>
							<div className="flex items-center gap-3">
								<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
									<Building2 className="w-5 h-5" />
								</div>
								<div>
									<DialogTitle>{popupTitle}</DialogTitle>
									<DialogDescription>Bulk add tenants</DialogDescription>
								</div>
							</div>
						</DialogHeader>
						<div className="p-5">
							<BulkTenantsAddPopup
								myCenters={myCenters}
								handlePostFleetLocations={handlePostFleetLocations}
							/>
						</div>
					</DialogContent>
				</Dialog>

				{/* Add Location Dialog */}
				<Dialog open={openAddLocationPopup} onClose={() => setOpenAddLocationPopup(false)}>
					<DialogContent className="max-w-2xl">
						<DialogHeader>
							<div className="flex items-center gap-3">
								<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
									<MapPinIcon className="w-5 h-5" />
								</div>
								<div>
									<DialogTitle>{popupTitle}</DialogTitle>
									<DialogDescription>Add a new location</DialogDescription>
								</div>
							</div>
						</DialogHeader>
						<div className="p-5">
							<LocationAddNew locationAdd={handleAddLocation} popupGuide="true" />
						</div>
					</DialogContent>
				</Dialog>

				{openMap && mapLocation && (
					<MapLocationPopupCN
						open={openMap}
						onClose={() => setOpenMap(false)}
						location={mapLocation}
					/>
				)}
			</>
		)
	}

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />

			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				<div className="flex-1 flex overflow-hidden">
					<main className="flex-1 overflow-y-auto p-6">
						<h2 className="text-2xl font-semibold text-gray-800 mb-4">Bulk Load</h2>
						{renderContent()}
					</main>
				</div>
			</div>

			{/* Hidden div for Google Places service */}
			<div ref={placesRef} className="hidden" />
		</div>
	)
}

// Helper: enrich a single location with Google Places data
async function enrichSingleLocation(service: any, OK: string, location: any): Promise<any | null> {
	const query = `${location.assetName || ''} ${location.city || ''}, ${location.stateProvince || ''}`

	return new Promise(resolve => {
		service.textSearch({ query }, (results: any[], status: any) => {
			if (status !== OK || !results || results.length === 0) {
				resolve(null)
				return
			}

			const placeId = results[0].place_id
			if (!placeId) {
				resolve(null)
				return
			}

			const enrichedData: any = {
				...location,
				googlePlaceId: placeId,
			}

			// Populate retailer: prefer parentRetailer from file, then Google Places name
			if (!location.retailer) {
				if (location.parentRetailer) {
					enrichedData.retailer = location.parentRetailer
				} else if (results[0].name) {
					enrichedData.retailer = results[0].name
				}
			}

			// Fetch photo from place details
			service.getDetails({ placeId, fields: ['photos'] }, async (detailResult: any, detailStatus: any) => {
				if (detailStatus === OK && detailResult?.photos && detailResult.photos.length > 0) {
					try {
						const photoUrl = detailResult.photos[0].getUrl({ maxWidth: 800 })
						const uploaded = await uploadToCloudinary(photoUrl)
						enrichedData.image = uploaded.secure_url
					} catch (err) {
						console.error('[Enrich] Photo upload failed:', err)
					}
				}
				resolve(enrichedData)
			})
		})
	})
}

export default BulkLoadLocationsCN
